/**
 * @fileoverview Context for AssignProjectModal state management
 */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { FC, ReactNode } from 'react'
import type { AssignProjectContextValue, AssignProjectState } from './types'

const getInitialState = (): AssignProjectState => ({
  startDate: new Date(),
  selectedGroupId: null,
  assignedStudentIds: [],
  filterGroupId: null,
  searchQuery: '',
  isSubmitting: false,
  isDirty: false,
})

const AssignProjectContext = createContext<AssignProjectContextValue | null>(null)

interface AssignProjectProviderProps {
  children: ReactNode
  opened: boolean
  /** Pre-loaded student IDs for edit mode. Optional — defaults to empty (assign mode). */
  initialStudentIds?: string[]
}

export const AssignProjectProvider: FC<AssignProjectProviderProps> = ({ children, opened, initialStudentIds }) => {
  const [state, setState] = useState<AssignProjectState>(getInitialState)
  const prevOpenedRef = useRef(opened)

  const reset = useCallback(() => setState({
    ...getInitialState(),
    assignedStudentIds: initialStudentIds || [],
  }), [initialStudentIds])

  // Reset state only on transition from closed to opened (not on every re-render)
  useEffect(() => {
    if (opened && !prevOpenedRef.current) {
      reset()
    }
    prevOpenedRef.current = opened
  }, [opened, reset])

  const setStartDate = useCallback((date: Date | null) => {
    setState(prev => ({ ...prev, startDate: date, isDirty: true }))
  }, [])

  const setSelectedGroupId = useCallback((id: string | null, options?: { silent?: boolean }) => {
    setState(prev => ({
      ...prev,
      selectedGroupId: id,
      filterGroupId: id,
      assignedStudentIds: options?.silent ? prev.assignedStudentIds : [],
      isDirty: options?.silent ? prev.isDirty : true,
    }))
  }, [])

  const setFilterGroupId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, filterGroupId: id }))
  }, [])

  const setSearchQuery = useCallback((query: string) => {
    setState(prev => ({ ...prev, searchQuery: query }))
  }, [])

  const setIsSubmitting = useCallback((value: boolean) => {
    setState(prev => ({ ...prev, isSubmitting: value }))
  }, [])

  const addStudent = useCallback((studentId: string) => {
    setState(prev => ({
      ...prev,
      assignedStudentIds: [...prev.assignedStudentIds, studentId],
      isDirty: true,
    }))
  }, [])

  const addAllStudents = useCallback((studentIds: string[]) => {
    setState(prev => ({
      ...prev,
      assignedStudentIds: [...new Set([...prev.assignedStudentIds, ...studentIds])],
      isDirty: true,
    }))
  }, [])

  const removeStudent = useCallback((studentId: string) => {
    setState(prev => ({
      ...prev,
      assignedStudentIds: prev.assignedStudentIds.filter(id => id !== studentId),
      isDirty: true,
    }))
  }, [])

  const value = useMemo<AssignProjectContextValue>(
    () => ({
      ...state,
      setStartDate,
      setSelectedGroupId,
      setFilterGroupId,
      setSearchQuery,
      setIsSubmitting,
      addStudent,
      addAllStudents,
      removeStudent,
      reset,
      hasChanges: state.isDirty,
    }),
    [
      state, setStartDate, setSelectedGroupId, setFilterGroupId,
      setSearchQuery, setIsSubmitting, addStudent, addAllStudents, removeStudent, reset,
    ],
  )

  return <AssignProjectContext.Provider value={value}>{children}</AssignProjectContext.Provider>
}

export const useAssignProjectContext = (): AssignProjectContextValue => {
  const context = useContext(AssignProjectContext)
  if (!context) {
    throw new Error('useAssignProjectContext must be used within AssignProjectProvider')
  }
  return context
}
